import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { fNumber } from '../../../../utils/formatNumber';
import modifyScriptName from '../../../../utils/modifyScriptName';

const ScriptDetailsModal = ({ open, onClose, script }) => {
    if (!script) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return timeString;
    };

    const tableData = [
        { label: 'Expiry Date', value: formatDate(script.expiry) },
        { label: 'Square Off Date', value: formatDate(script.squareoff_date) },
        ...(script.exchange ? [{ label: 'Trading Hours', value: `${formatTime(script.exchange.startTime)} - ${formatTime(script.exchange.endTime)}` }] : []),
        ...(script.ohlc ? [
            { label: 'Open', value: fNumber(script.ohlc.open || 0) },
            { label: 'High', value: fNumber(script.ohlc.high || 0), color: 'success.main' },
            { label: 'Low', value: fNumber(script.ohlc.low || 0), color: 'error.main' },
        ] : []),
        { label: 'Last Price', value: fNumber(script.last_price || 0) },
        ...(script.depth ? [
            { label: 'Bid Price', value: fNumber(script.depth?.buy?.[0]?.price || 0), color: 'success.main' },
            { label: 'Ask Price', value: fNumber(script.depth?.sell?.[0]?.price || 0), color: 'error.main' },
        ] : []),
        ...(script.chg !== undefined ? [{ label: 'Change', value: fNumber(script.chg), color: script.chg >= 0 ? 'success.main' : 'error.main' }] : []),
    ].filter(Boolean);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                }
            }}
        >
            <DialogTitle sx={{ py: 1.5, px: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.1rem' }}>
                            {`${modifyScriptName(script.tradingsymbol, script.expiry, script)} (${script.exchange?.name || ''})`}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 0, py: 1 }}>
                <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.75, px: 2 } }}>
                        <TableBody>
                            {tableData.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:nth-of-type(even)': {
                                            backgroundColor: 'action.hover'
                                        },
                                        '&:hover': {
                                            backgroundColor: 'action.selected'
                                        }
                                    }}
                                >
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.8rem',
                                            color: 'text.secondary',
                                            width: '40%'
                                        }}
                                    >
                                        {row.label}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            color: row.color || 'text.primary',
                                            textAlign: 'right'
                                        }}
                                    >
                                        {row.value}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                        px: 3,
                        py: 0.5,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'none'
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScriptDetailsModal;